// ProShareCard — carte visuelle AYA PRO générée (canvas 1080×1350) à partager sur WhatsApp (v5.27.0).
// Annonce l'espace pro : logo officiel, photo/icône, nom, métier, services, commune, bandeau AYA.
import { useEffect, useRef, useState } from "react";
import { Download, X } from "lucide-react";
import { toast } from "sonner";

const LOGO_SRC = "/aya/branding/aya-pro-logo.png";
const loadImg = (src) => new Promise((res) => {
  if (!src) return res(null);
  const i = new Image();
  i.onload = () => res(i); i.onerror = () => res(null);
  i.src = src;
});

const rounded = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

async function drawCard(canvas, d) {
  const W = 1080, H = 1350;
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const grad = ctx.createRadialGradient(W * 0.3, 0, 100, W * 0.5, H * 0.4, H);
  grad.addColorStop(0, "#0B4A2F"); grad.addColorStop(1, "#04170E");
  ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
  ctx.textAlign = "center";

  const [logo, photo] = await Promise.all([loadImg(LOGO_SRC), loadImg(d.photo)]);
  if (logo) {
    const lw = 360, lh = lw * (logo.height / logo.width);
    ctx.drawImage(logo, (W - lw) / 2, 78, lw, lh);
  }
  ctx.fillStyle = "#F5B301"; ctx.font = "800 30px Manrope, sans-serif";
  ctx.save(); ctx.letterSpacing = "12px";
  ctx.fillText(d.kicker || "ESPACE PROFESSIONNEL", W / 2, 255);
  ctx.restore();

  // Visuel : photo ronde ou icône sur pastille pastel
  const cy = 480, r = 170;
  ctx.save();
  ctx.beginPath(); ctx.arc(W / 2, cy, r, 0, Math.PI * 2); ctx.closePath();
  ctx.fillStyle = d.bg || "#E8F8EF"; ctx.fill(); ctx.clip();
  if (photo) {
    const k = Math.max((2 * r) / photo.width, (2 * r) / photo.height);
    ctx.drawImage(photo, W / 2 - (photo.width * k) / 2, cy - (photo.height * k) / 2, photo.width * k, photo.height * k);
  } else {
    ctx.font = "160px sans-serif"; ctx.fillText(d.icon || "💼", W / 2, cy + 58);
  }
  ctx.restore();
  ctx.beginPath(); ctx.arc(W / 2, cy, r + 7, 0, Math.PI * 2);
  ctx.strokeStyle = "#F5B301"; ctx.lineWidth = 7; ctx.stroke();

  // Nom · métier · services · commune
  ctx.fillStyle = "#FFFFFF"; ctx.font = "900 66px Manrope, sans-serif";
  const name = String(d.name || "").slice(0, 28);
  ctx.fillText(name, W / 2, 760);
  ctx.fillStyle = "#F5B301"; ctx.font = "800 38px Manrope, sans-serif";
  ctx.fillText(d.activityLabel || "", W / 2, 822);
  if (d.offerings?.length) {
    ctx.fillStyle = "#A7E3C4"; ctx.font = "600 32px Manrope, sans-serif";
    ctx.fillText(d.offerings.slice(0, 3).join("  ·  ").slice(0, 52), W / 2, 884);
  }
  if (d.commune) {
    ctx.fillStyle = "#FFFFFF"; ctx.font = "700 36px Manrope, sans-serif";
    ctx.fillText(`📍 ${d.commune}${d.quartier ? `, ${d.quartier}` : ""}`, W / 2, 952);
  }

  // Bandeau bas blanc arrondi
  rounded(ctx, 70, 1030, W - 140, 200, 28);
  ctx.fillStyle = "#FFFFFF"; ctx.fill();
  ctx.fillStyle = "#0E8A4F"; ctx.font = "900 42px Manrope, sans-serif";
  ctx.fillText(d.ctaTitle || "Retrouvez-moi sur AYA", W / 2, 1110);
  ctx.fillStyle = "#374151"; ctx.font = "600 30px Manrope, sans-serif";
  ctx.fillText(d.ctaSub || "Recherchez mon activité dans AYA Search", W / 2, 1162);
  ctx.fillStyle = "#A7E3C4"; ctx.font = "700 26px Manrope, sans-serif";
  ctx.fillText("✅ Professionnel référencé AYA · Côte d'Ivoire", W / 2, 1292);
}

export const ProShareSheet = ({ data, onClose }) => {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => { drawCard(canvasRef.current, data).then(() => setReady(true)); }, [data]);

  const toBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
  const waText = data.waText || `📣 *${data.name}* est maintenant sur AYA PRO !\n${data.activityLabel}${data.commune ? ` · 📍 ${data.commune}` : ""}\n\nRetrouvez-moi sur AYA pour me contacter facilement 🇨🇮`;

  const shareWhatsApp = async () => {
    const blob = await toBlob();
    const file = new File([blob], "aya-pro.png", { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], text: waText }); return; } catch {}
    }
    download();
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(waText)}`, "_blank", "noopener");
    toast.info("Image téléchargée — joignez-la à votre message WhatsApp");
  };
  const download = async () => {
    const blob = await toBlob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `aya-pro-${String(data.name || "carte").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
    a.click();
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9100, background: "rgba(4,23,14,0.88)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onClose} data-testid="pro-share-sheet">
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 400, maxHeight: "92vh", overflowY: "auto", background: "#fff", borderRadius: 18, padding: 15, boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: 14.5, fontWeight: 900, color: "#111827" }}>📣 Annoncez votre espace</span>
          <button onClick={onClose} aria-label="Fermer" data-testid="pro-share-close"
            style={{ width: 30, height: 30, borderRadius: "50%", border: "1px solid #E5E7EB", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={14} />
          </button>
        </div>
        <canvas ref={canvasRef} data-testid="pro-share-canvas"
          style={{ width: "100%", borderRadius: 14, display: "block", boxShadow: "0 4px 18px rgba(0,0,0,0.18)" }} />
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          <button onClick={shareWhatsApp} disabled={!ready} data-testid="pro-share-whatsapp"
            style={{ padding: "13px 16px", borderRadius: 12, border: "none", background: "#25D366", color: "#fff", fontSize: 13.5, fontWeight: 800, cursor: "pointer", opacity: ready ? 1 : 0.5 }}>
            💬 Partager sur WhatsApp
          </button>
          <button onClick={download} disabled={!ready} data-testid="pro-share-download"
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "12px 16px", borderRadius: 12, border: "1.5px solid #0E8A4F", background: "#fff", color: "#0E8A4F", fontSize: 13, fontWeight: 800, cursor: "pointer", opacity: ready ? 1 : 0.5 }}>
            <Download size={14} /> Télécharger l'image
          </button>
        </div>
      </div>
    </div>
  );
};
